import * as THREE from "three";
import {
	clampTimeOfDay,
	type LegacyTimeOfDay,
	normalizeTimeOfDay,
	type TimeOfDayValue,
} from "../../domain/viewer/timeOfDay";
import { GRAVEL_LIFT_M } from "./landscapeGeometry";

/**
 * Dusk accent lighting — the warm LED lines under the lavar and along the
 * sockel that make a glazed sauna read as *lit from within* at dusk, the
 * way every premium sauna render is photographed.
 *
 * Presentation only, like the god rays: nothing here reaches the BOM, the
 * engines or the offscreen build-step snapshots. It is a viewer annotation
 * derived from the BUILT scene (the same idea as the scale figures — see
 * the 2026-07-28 "overlay that measures the built scene" note), so it
 * follows every engine's benches and base trim without per-engine math.
 *
 * This module owns the pure parts — where each strip and light pool sits
 * and how strongly it glows — so they can be asserted against real
 * geometry without a WebGL context. `SceneAccentLighting.tsx` draws them.
 */

/** Seat slats — one box per bench id, exactly as the scale figures read them. */
const BENCH_SLAT_PREFIX = "detail/bench-slat/";
/** The bench's front rail, e.g. `detail/bench-rail/square-upper-front`. */
const BENCH_FRONT_RAIL_NAME = /^detail\/bench-rail\/(.+)-front$/;
/** Room floor slab — the surface the lowest bench's light lands on. */
const FLOOR_PANEL_PREFIX = "detail/floor-panel/";
/** Sockel trim along the bottom of each facade, e.g. `…/two-room-left`. */
const BASE_TRIM_PREFIX = "detail/base-trim/";
/** Facade cladding — its outer face is the line a deck meets. */
const CLADDING_PREFIX = "detail/cladding/";
/** Deck boards, e.g. `detail/deck/board-1-3`. */
const DECK_BOARD_PREFIX = "detail/deck/board-";
/** Interior wall panelling — the surfaces a backrest light washes up. */
const WALL_PANEL_PREFIXES = ["detail/panel/", "detail/divider-panel/"];

/**
 * Accent lights stay dark through the day and fade in across dusk, a
 * little after the god rays start (0.55): LEDs are switched on when the
 * sky has dimmed, not in full sun. Smoothstepped, no pop-in.
 */
const ACCENT_FADE_IN_START = 0.6;
const ACCENT_FADE_IN_END = 0.86;

/** LED profile cross-section. A real aluminium LED channel is ~10–17 mm. */
export const ACCENT_STRIP_THICKNESS_M = 0.012;
/** Tuck the bench strip this far behind the front rail's back face. */
const BENCH_STRIP_TUCK_M = 0.012;
/** Keep the strip ends this far short of the rail ends. */
const BENCH_STRIP_END_INSET_M = 0.03;
/** Half-depth of the light pool cast on the surface under a bench strip. */
const BENCH_POOL_HALF_DEPTH_M = 0.34;
/** Lift pools off the surface they lie on so they never z-fight it. */
const SURFACE_LIFT_M = 0.004;
/**
 * How far up the wall the backrest light reaches above the seat. A strip
 * hidden behind the top board of a lav throws a wash about this tall.
 */
const WALL_WASH_HEIGHT_M = 0.6;
/** A seat further than this from the wall behind it gets no wash. */
const WALL_WASH_MAX_GAP_M = 0.12;
/** Panel strips thinner than this along the bench's depth are wall panels. */
const WALL_PANEL_MAX_THICKNESS_M = 0.05;
/** How far the light from the facade's foot reaches out across a deck. */
const DECK_POOL_OUTWARD_M = 0.9;
/** A deck board this close to a facade line counts as meeting that wall. */
const DECK_CONTACT_GAP_M = 0.1;
/** Base strip sits this far in from the base trim's outer face. */
const BASE_STRIP_TUCK_M = 0.01;
/** How far the ground pool reaches out from the facade line. */
const BASE_POOL_OUTWARD_M = 0.8;
/** How far the ground pool reaches back under the building. */
const BASE_POOL_INWARD_M = 0.3;
/** Base pools overrun the trim ends so the corners meet softly. */
const BASE_POOL_END_OVERRUN_M = 0.25;
/**
 * Ground pools float this far above the terrain: over the gravel pad the
 * scene lays around the footprint, plus a margin for its micro-relief.
 */
const GROUND_LIFT_M = GRAVEL_LIFT_M + 0.016;

export type Vec3Tuple = [number, number, number];

/** A thin emissive LED line. */
export interface AccentStrip {
	id: string;
	kind: "bench" | "base" | "deck";
	center: Vec3Tuple;
	/** Unit axis the strip runs along (horizontal). */
	along: Vec3Tuple;
	length: number;
}

/**
 * A soft, additive light pool lying on a surface. The quad spans `length`
 * along `along` and `depth` along `across`; brightness peaks at `peak`
 * (0..1 across the depth) and falls off to zero at both edges, and fades
 * out over the last few percent of the length so neighbouring pools never
 * show a seam. Bench and base pools lie on horizontal surfaces; a `wall`
 * pool stands on the wall behind a lav with `across` pointing up.
 */
export interface AccentPool {
	id: string;
	kind: "bench" | "base" | "wall" | "deck";
	center: Vec3Tuple;
	along: Vec3Tuple;
	/** Unit axis the gradient runs along (⟂ `along`; up for a wall wash). */
	across: Vec3Tuple;
	length: number;
	depth: number;
	/** 0..1 position of the brightest line across the depth. */
	peak: number;
	/** Relative strength, multiplied by the time-of-day intensity. */
	strength: number;
}

export interface AccentLightingPlan {
	strips: AccentStrip[];
	pools: AccentPool[];
}

function smoothstep(edge0: number, edge1: number, x: number): number {
	const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
	return t * t * (3 - 2 * t);
}

/**
 * Global 0..1 strength of the accent lights at a time of day. Zero in
 * daylight, so the default (midday) scene — and every visual baseline
 * captured at it — is untouched.
 */
export function getAccentLightingIntensity(
	timeOfDay: TimeOfDayValue | LegacyTimeOfDay | number,
): number {
	const t = clampTimeOfDay(normalizeTimeOfDay(timeOfDay));
	if (t <= ACCENT_FADE_IN_START) return 0;
	return smoothstep(ACCENT_FADE_IN_START, ACCENT_FADE_IN_END, t);
}

function isRenderable(object: THREE.Object3D, root: THREE.Object3D): boolean {
	let node: THREE.Object3D | null = object;
	while (node) {
		if (!node.visible) return false;
		if (node === root) return true;
		node = node.parent;
	}
	return true;
}

interface SceneAccentBoxes {
	seats: Map<string, THREE.Box3>;
	frontRails: Map<string, THREE.Box3>;
	floor: THREE.Box3;
	baseTrims: Map<string, THREE.Box3>;
	wallPanels: THREE.Box3[];
	deckBoards: THREE.Box3[];
	cladding: THREE.Box3;
}

/**
 * One walk of the model, measuring every box in `frame`'s LOCAL space.
 * The accent group hangs inside the sauna rig, so measuring in the rig's
 * frame keeps the strips glued to the benches when the building turns.
 */
function readAccentBoxes(
	root: THREE.Object3D,
	frame: THREE.Object3D,
): SceneAccentBoxes {
	const boxes: SceneAccentBoxes = {
		seats: new Map(),
		frontRails: new Map(),
		floor: new THREE.Box3(),
		baseTrims: new Map(),
		wallPanels: [],
		deckBoards: [],
		cladding: new THREE.Box3(),
	};
	root.updateWorldMatrix(true, true);
	frame.updateWorldMatrix(true, false);
	const toFrame = new THREE.Matrix4().copy(frame.matrixWorld).invert();
	const toFrameFromMesh = new THREE.Matrix4();

	const measure = (object: THREE.Object3D): THREE.Box3 | null => {
		const mesh = object as THREE.Mesh;
		const geometry = mesh.geometry as THREE.BufferGeometry | undefined;
		if (!geometry) return null;
		if (!geometry.boundingBox) geometry.computeBoundingBox();
		if (!geometry.boundingBox) return null;
		toFrameFromMesh.multiplyMatrices(toFrame, object.matrixWorld);
		return geometry.boundingBox.clone().applyMatrix4(toFrameFromMesh);
	};

	const grow = (map: Map<string, THREE.Box3>, id: string, box: THREE.Box3) => {
		const existing = map.get(id);
		if (existing) existing.union(box);
		else map.set(id, box);
	};

	root.traverse((object) => {
		const name = object.name;
		if (!name.startsWith("detail/")) return;
		if (name.startsWith(BENCH_SLAT_PREFIX)) {
			if (!isRenderable(object, root)) return;
			const box = measure(object);
			if (!box) return;
			const id = name.slice(BENCH_SLAT_PREFIX.length).replace(/-\d+$/, "");
			grow(boxes.seats, id, box);
			return;
		}
		const rail = name.match(BENCH_FRONT_RAIL_NAME);
		if (rail) {
			if (!isRenderable(object, root)) return;
			const box = measure(object);
			if (box) grow(boxes.frontRails, rail[1], box);
			return;
		}
		if (name.startsWith(FLOOR_PANEL_PREFIX)) {
			const box = measure(object);
			if (box) boxes.floor.union(box);
			return;
		}
		if (name.startsWith(BASE_TRIM_PREFIX)) {
			if (!isRenderable(object, root)) return;
			const box = measure(object);
			if (box) grow(boxes.baseTrims, name.slice(BASE_TRIM_PREFIX.length), box);
			return;
		}
		if (name.startsWith(CLADDING_PREFIX)) {
			const box = measure(object);
			if (box) boxes.cladding.union(box);
			return;
		}
		if (name.startsWith(DECK_BOARD_PREFIX)) {
			if (!isRenderable(object, root)) return;
			const box = measure(object);
			if (box) boxes.deckBoards.push(box);
			return;
		}
		if (WALL_PANEL_PREFIXES.some((prefix) => name.startsWith(prefix))) {
			if (!isRenderable(object, root)) return;
			const box = measure(object);
			if (box) boxes.wallPanels.push(box);
		}
	});

	return boxes;
}

type HorizontalAxis = "x" | "z";

function longAxisOf(box: THREE.Box3): HorizontalAxis {
	return box.max.x - box.min.x >= box.max.z - box.min.z ? "x" : "z";
}

function unit(axis: HorizontalAxis, sign = 1): Vec3Tuple {
	return axis === "x" ? [sign, 0, 0] : [0, 0, sign];
}

function otherAxis(axis: HorizontalAxis): HorizontalAxis {
	return axis === "x" ? "z" : "x";
}

/**
 * The top of whatever the light from a strip at (`x`, `z`, below `y`)
 * lands on: the highest OTHER bench seat directly beneath it, else the
 * floor. A lower lav in front of an upper one catches the upper lav's
 * light on its seat — which is exactly how the lit tiers read in a
 * real sauna photographed at dusk.
 */
function surfaceBelow(
	x: number,
	z: number,
	y: number,
	selfId: string,
	seats: Map<string, THREE.Box3>,
	floorTopY: number | null,
): number | null {
	let best: number | null = floorTopY;
	for (const [id, seat] of seats) {
		if (id === selfId) continue;
		if (seat.max.y >= y) continue;
		if (x < seat.min.x || x > seat.max.x) continue;
		if (z < seat.min.z || z > seat.max.z) continue;
		if (best === null || seat.max.y > best) best = seat.max.y;
	}
	return best;
}

/**
 * The room-facing face of the wall panelling directly behind a seat, or
 * null when the seat does not stand against a wall (a lower lav in front
 * of an upper one, or a seat backed by glass).
 */
function wallFaceBehind(
	seat: THREE.Box3,
	along: HorizontalAxis,
	across: HorizontalAxis,
	frontSign: number,
	wallPanels: THREE.Box3[],
): number | null {
	const seatBack = frontSign > 0 ? seat.min[across] : seat.max[across];
	let best: number | null = null;
	for (const panel of wallPanels) {
		if (panel.max[across] - panel.min[across] > WALL_PANEL_MAX_THICKNESS_M) {
			continue;
		}
		if (
			panel.max[along] <= seat.min[along] ||
			panel.min[along] >= seat.max[along]
		) {
			continue;
		}
		// The panel must rise past the whole wash, starting at seat level.
		if (panel.min.y > seat.max.y + 0.05) continue;
		if (panel.max.y < seat.max.y + WALL_WASH_HEIGHT_M) continue;
		const face = frontSign > 0 ? panel.max[across] : panel.min[across];
		// Behind the seat's back edge (a hair of overlap allowed), and close.
		const gap = frontSign > 0 ? seatBack - face : face - seatBack;
		if (gap < -0.01 || gap > WALL_WASH_MAX_GAP_M) continue;
		if (
			best === null ||
			Math.abs(face - seatBack) < Math.abs(best - seatBack)
		) {
			best = face;
		}
	}
	return best;
}

function planBenchAccents(
	boxes: SceneAccentBoxes,
	strips: AccentStrip[],
	pools: AccentPool[],
): void {
	const floorTopY = boxes.floor.isEmpty() ? null : boxes.floor.max.y;
	const roomCenter = new THREE.Vector3();
	if (!boxes.floor.isEmpty()) {
		boxes.floor.getCenter(roomCenter);
	} else {
		const spread = new THREE.Box3();
		for (const seat of boxes.seats.values()) spread.union(seat);
		spread.getCenter(roomCenter);
	}

	const ids = [...boxes.seats.keys()].sort();
	for (const id of ids) {
		const seat = boxes.seats.get(id);
		if (!seat || seat.isEmpty()) continue;
		// Fall back to the seat's own front edge for an engine that draws
		// no separate front rail.
		const rail = boxes.frontRails.get(id) ?? seat;
		const along = longAxisOf(seat);
		const across = otherAxis(along);
		const seatCenter = seat.getCenter(new THREE.Vector3());
		// "Front" is the side facing the room; the strip tucks in behind
		// the rail (towards the wall), under the seat, out of direct view.
		const frontSign = roomCenter[across] >= seatCenter[across] ? 1 : -1;
		const railBack = frontSign > 0 ? rail.min[across] : rail.max[across];
		const stripAcross = railBack - frontSign * BENCH_STRIP_TUCK_M;
		const stripY = rail.min.y - ACCENT_STRIP_THICKNESS_M / 2;
		const alongMin = rail.min[along] + BENCH_STRIP_END_INSET_M;
		const alongMax = rail.max[along] - BENCH_STRIP_END_INSET_M;
		const length = alongMax - alongMin;
		if (length <= 0.05) continue;
		const alongMid = (alongMin + alongMax) / 2;

		const center: Vec3Tuple = [0, stripY, 0];
		center[along === "x" ? 0 : 2] = alongMid;
		center[across === "x" ? 0 : 2] = stripAcross;
		strips.push({
			id: `bench/${id}`,
			kind: "bench",
			center,
			along: unit(along),
			length,
		});

		const surfaceY = surfaceBelow(
			center[0],
			center[2],
			stripY,
			id,
			boxes.seats,
			floorTopY,
		);
		if (surfaceY === null) continue;
		const poolCenter: Vec3Tuple = [
			center[0],
			surfaceY + SURFACE_LIFT_M,
			center[2],
		];
		pools.push({
			id: `bench/${id}`,
			kind: "bench",
			center: poolCenter,
			along: unit(along),
			across: unit(across, frontSign),
			length: length + 0.1,
			depth: BENCH_POOL_HALF_DEPTH_M * 2,
			peak: 0.5,
			strength: 1,
		});

		// Backrest light: a lav against a wall washes that wall upward from
		// the seat, the warm band behind the sitters in every dusk sauna
		// photograph.
		const wallFace = wallFaceBehind(
			seat,
			along,
			across,
			frontSign,
			boxes.wallPanels,
		);
		if (wallFace === null) continue;
		const washCenter: Vec3Tuple = [0, seat.max.y + WALL_WASH_HEIGHT_M / 2, 0];
		washCenter[along === "x" ? 0 : 2] = (seat.min[along] + seat.max[along]) / 2;
		washCenter[across === "x" ? 0 : 2] = wallFace + frontSign * SURFACE_LIFT_M;
		pools.push({
			id: `wall/${id}`,
			kind: "wall",
			center: washCenter,
			along: unit(along),
			across: [0, 1, 0],
			length: seat.max[along] - seat.min[along],
			depth: WALL_WASH_HEIGHT_M,
			peak: 0,
			strength: 0.8,
		});
	}
}

function planBaseAccents(
	boxes: SceneAccentBoxes,
	groundY: number | null,
	strips: AccentStrip[],
	pools: AccentPool[],
): void {
	if (boxes.baseTrims.size === 0) return;
	const footprint = new THREE.Box3();
	for (const trim of boxes.baseTrims.values()) footprint.union(trim);
	const footprintCenter = footprint.getCenter(new THREE.Vector3());

	const ids = [...boxes.baseTrims.keys()].sort();
	for (const id of ids) {
		const trim = boxes.baseTrims.get(id);
		if (!trim || trim.isEmpty()) continue;
		const along = longAxisOf(trim);
		const across = otherAxis(along);
		const trimCenter = trim.getCenter(new THREE.Vector3());
		const outSign = trimCenter[across] >= footprintCenter[across] ? 1 : -1;
		const outerFace = outSign > 0 ? trim.max[across] : trim.min[across];
		const length = trim.max[along] - trim.min[along];
		if (length <= 0.05) continue;

		const center: Vec3Tuple = [0, trim.min.y - ACCENT_STRIP_THICKNESS_M / 2, 0];
		center[along === "x" ? 0 : 2] = trimCenter[along];
		center[across === "x" ? 0 : 2] = outerFace - outSign * BASE_STRIP_TUCK_M;
		strips.push({
			id: `base/${id}`,
			kind: "base",
			center,
			along: unit(along),
			length,
		});

		if (groundY === null) continue;
		// The pool's depth runs from under the building (0) out into the
		// yard (1); the facade line is the bright edge.
		const depth = BASE_POOL_INWARD_M + BASE_POOL_OUTWARD_M;
		const poolAcross =
			outerFace + outSign * ((BASE_POOL_OUTWARD_M - BASE_POOL_INWARD_M) / 2);
		const poolCenter: Vec3Tuple = [0, groundY + GROUND_LIFT_M, 0];
		poolCenter[along === "x" ? 0 : 2] = trimCenter[along];
		poolCenter[across === "x" ? 0 : 2] = poolAcross;
		pools.push({
			id: `base/${id}`,
			kind: "base",
			center: poolCenter,
			along: unit(along),
			across: unit(across, outSign),
			length: length + BASE_POOL_END_OVERRUN_M * 2,
			depth,
			peak: BASE_POOL_INWARD_M / depth,
			strength: 1,
		});
	}
}

/**
 * Where a deck meets a facade the engine drops that wall's base trim (and
 * with it the sockel strip). The LED moves to the deck instead: a line at
 * the foot of the facade, on the boards, and a pool of light across the
 * boards from the wall out — the lit terrace edge of a dusk sauna photo.
 */
function planDeckAccents(
	boxes: SceneAccentBoxes,
	strips: AccentStrip[],
	pools: AccentPool[],
): void {
	if (boxes.deckBoards.length === 0 || boxes.baseTrims.size === 0) return;
	// The facade's outer face. The base trims alone stop short on the side
	// whose trim the deck suppressed.
	const footprint = boxes.cladding.clone();
	for (const trim of boxes.baseTrims.values()) footprint.union(trim);
	const trimmedSides = new Set<string>();
	for (const trim of boxes.baseTrims.values()) {
		const along = longAxisOf(trim);
		const across = otherAxis(along);
		const mid = (trim.min[across] + trim.max[across]) / 2;
		const center = (footprint.min[across] + footprint.max[across]) / 2;
		trimmedSides.add(`${across}${mid >= center ? "+" : "-"}`);
	}

	for (const across of ["x", "z"] as const) {
		const along = otherAxis(across);
		for (const sign of [1, -1]) {
			if (trimmedSides.has(`${across}${sign > 0 ? "+" : "-"}`)) continue;
			const facade = sign > 0 ? footprint.max[across] : footprint.min[across];
			// Boards that start at this facade line and run away from it.
			const touching = boxes.deckBoards.filter((board) => {
				const near = sign > 0 ? board.min[across] : board.max[across];
				const far = sign > 0 ? board.max[across] : board.min[across];
				return (
					Math.abs(near - facade) <= DECK_CONTACT_GAP_M &&
					sign * (far - facade) > 0 &&
					board.max[along] > footprint.min[along] &&
					board.min[along] < footprint.max[along]
				);
			});
			if (touching.length === 0) continue;
			const deck = new THREE.Box3();
			for (const board of touching) deck.union(board);
			const alongMin = Math.max(deck.min[along], footprint.min[along]);
			const alongMax = Math.min(deck.max[along], footprint.max[along]);
			const length = alongMax - alongMin;
			if (length <= 0.05) continue;
			const alongMid = (alongMin + alongMax) / 2;
			const deckTop = deck.max.y;
			const id = `deck/${across}${sign > 0 ? "+" : "-"}`;

			const stripCenter: Vec3Tuple = [
				0,
				deckTop + ACCENT_STRIP_THICKNESS_M / 2,
				0,
			];
			stripCenter[along === "x" ? 0 : 2] = alongMid;
			stripCenter[across === "x" ? 0 : 2] =
				facade + sign * (ACCENT_STRIP_THICKNESS_M / 2);
			strips.push({
				id,
				kind: "deck",
				center: stripCenter,
				along: unit(along),
				length,
			});

			const reach = Math.min(
				DECK_POOL_OUTWARD_M,
				Math.abs((sign > 0 ? deck.max[across] : deck.min[across]) - facade),
			);
			const poolCenter: Vec3Tuple = [0, deckTop + SURFACE_LIFT_M, 0];
			poolCenter[along === "x" ? 0 : 2] = alongMid;
			poolCenter[across === "x" ? 0 : 2] = facade + (sign * reach) / 2;
			pools.push({
				id,
				kind: "deck",
				center: poolCenter,
				along: unit(along),
				across: unit(across, sign),
				length,
				depth: reach,
				peak: 0,
				// Dark deck boards swallow an additive pool at the sockel
				// pools' strength; this reads as the same light on them.
				strength: 1.4,
			});
		}
	}
}

/**
 * Plan every accent strip and pool for the built model, in `frame`'s
 * local space.
 *
 * `groundY` is the ground plane's height in that frame, or `null` when
 * there is no flat ground to paint on (a sloping plot): the base strips
 * still glow, but no pool is laid that would float over or cut into a
 * slope.
 */
export function planAccentLighting(
	root: THREE.Object3D | null | undefined,
	frame: THREE.Object3D | null | undefined,
	groundY: number | null,
): AccentLightingPlan {
	const plan: AccentLightingPlan = { strips: [], pools: [] };
	if (!root || !frame) return plan;
	const boxes = readAccentBoxes(root, frame);
	planBenchAccents(boxes, plan.strips, plan.pools);
	planBaseAccents(boxes, groundY, plan.strips, plan.pools);
	planDeckAccents(boxes, plan.strips, plan.pools);
	return plan;
}
